/** @file mirrorPose: a whole pose mirrored, twins swapped, sided nodes without a twin warned. */
import type { NodeKey } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { mirrorNodeKey } from './mirrorNodeKey';

const SIDED = /(L|R|Left|Right)$/;

/** Each node's mirrored key lands on its twin (in place without one); a sided name without a twin warns `NO_MIRROR_TWIN`. */
export function mirrorPose(puppet: Pick<Puppet, 'nodes'>, pose: Readonly<Record<string, NodeKey>>): { pose: Record<string, NodeKey>; warnings: Warning[] } {
  const out: Record<string, NodeKey> = {};
  const warnings: Warning[] = [];
  for (const [n, key] of Object.entries(pose)) {
    const twin = puppet.nodes[n]?.mirror;
    const parentTwin = typeof key.parent === 'string' ? puppet.nodes[key.parent]?.mirror : undefined;
    out[twin ?? n] = mirrorNodeKey(key, parentTwin);
    if (twin === undefined && SIDED.test(n)) {
      warnings.push({
        code: 'NO_MIRROR_TWIN', path: `/puppet/nodes/${n}`, params: { node: n },
        message: `node ${n} looks sided but has no mirror twin; it was mirrored in place`, hint: 'node.mirrorPair the node with its twin',
      });
    }
  }
  return { pose: out, warnings };
}
